import { setCookie } from 'cookies-next';

export type TealiumPayload = {
  data: {
    [key: string]: unknown;
  };
};

const setCookies = () => {
  const dataString = localStorage.getItem('tealium_va');

  if (dataString) {
    const data = JSON.parse(dataString);

    const cookieOptions = {
      maxAge: 60 * 60 * 24 * 30, // 30 days in seconds
    };

    setCookie(
      'visitor_audiences',
      Object.getOwnPropertyNames(data.audiences).join('&'),
      cookieOptions,
    );

    setCookie(
      'visitor_badges',
      Object.getOwnPropertyNames(data.badges).join('&'),
      cookieOptions,
    );

    setCookie(
      'visitor_properties',
      Object.getOwnPropertyNames(data.properties)
        .map(p => `${p}=${encodeURIComponent(data.properties[p])}`)
        .join('&'),
      cookieOptions,
    );

    setCookie(
      'visitor_metric_sets',
      encodeURIComponent(
        data.metric_sets !== undefined && data.metric_sets !== null
          ? JSON.stringify(data.metric_sets)
          : '',
      ),
      cookieOptions,
    );
  }
};

const UTAG_READY_POLL_INTERVAL_MS = 250;
const UTAG_READY_TIMEOUT_MS = 15000;

/**
 * utag.js is injected via the site settings' third-party script markup, which
 * is loaded lazily (on window load / requestIdleCallback), so window.utag
 * usually isn't defined yet when a page's mount effect fires. Poll for it
 * briefly instead of dropping the call.
 */
const runWhenUtagReady = (
  method: 'link' | 'view',
  run: () => void,
  startedAt = Date.now(),
) => {
  if (typeof window === 'undefined') return;

  if (typeof window.utag?.[method] === 'function') {
    run();

    return;
  }

  if (Date.now() - startedAt >= UTAG_READY_TIMEOUT_MS) return;

  setTimeout(
    () => runWhenUtagReady(method, run, startedAt),
    UTAG_READY_POLL_INTERVAL_MS,
  );
};

export const tealiumLink = (payload: TealiumPayload | null) => {
  if (!payload) return;

  runWhenUtagReady('link', () => {
    window.utag?.link({
      ...payload.data,
    });
  });
};

let latestViewToken = 0;

export const tealiumView = (payload: TealiumPayload | null) => {
  const token = ++latestViewToken;

  runWhenUtagReady('view', () => {
    // If a newer page mounted and called tealiumView while this one was still
    // waiting for utag, drop this stale payload instead of recording a view
    // for a page the user has already navigated away from.
    if (token === latestViewToken && payload) {
      window.utag?.view({
        ...payload.data,
      });
    }

    setCookies();
  });
};
